import {
  Action,
  ActionReducer,
  ActionReducerMap,
  createFeatureSelector,
  MetaReducer,
} from '@ngrx/store';

import { State as AppState, appReducer } from './app.reducer';
import { State as CmsState, cmsReducer } from './cms.reducer';
import {
  State as AvailabilityState,
  availabilityReducer,
} from './availability.reducer';
import { State as CultureState, cultureReducer } from './culture.reducer';
import { State as BookingState, bookingReducer } from './booking.reducer';
import { State as UserState, userReducer } from './user.reducer';
import { State as BagsState, bagsReducer } from './bags.reducer';
import { State as ExtrasState, extrasReducer } from './extras.reducer';
import { State as SeatState, seatReducer } from './seat.reducer';
import { State as MealsState, mealsReducer } from './meals.reducer';
import { State as ResourceState, resourceReducer } from './resource.reducer';
import { State as PaymentState, paymentReducer } from './payment.reducer';
import {
  State as PreviousRouteState,
  previousRouteReducer,
} from './previous-route.reducer';
import {
  State as SmsVerificationState,
  smsVerificationReducer,
} from './sms-verification.reducer';
import { State as StationState, stationReducer } from './station.reducer';
import {
  State as FlightInfoState,
  flightInfoReducer,
} from './flight-info.reducer';
import { headersReducer, State as HeadersState } from './headers.reducer';
import { apoReducer, State as ApoState } from './apo.reducer';
import { environment } from 'src/environments/environment';

import { INITIAL_STATE as APP_INITIAL_STATE } from './app.reducer';
import { INITIAL_STATE as CMS_INITIAL_STATE } from './cms.reducer';
import { INITIAL_STATE as AVAILABILITY_INITIAL_STATE } from './availability.reducer';
import { INITIAL_STATE as CULTURE_INITIAL_STATE } from './culture.reducer';
import { INITIAL_STATE as BOOKING_INITIAL_STATE } from './booking.reducer';
import { INITIAL_STATE as USER_INITIAL_STATE } from './user.reducer';
import { INITIAL_STATE as BAGS_INITIAL_STATE } from './bags.reducer';
import { INITIAL_STATE as EXTRAS_INITIAL_STATE } from './extras.reducer';
import { INITIAL_STATE as SEAT_INITIAL_STATE } from './seat.reducer';
import { INITIAL_STATE as MEALS_INITIAL_STATE } from './meals.reducer';
import { INITIAL_STATE as RESOURCE_INITIAL_STATE } from './resource.reducer';
import { INITIAL_STATE as PAYMENT_INITIAL_STATE } from './payment.reducer';
import { INITIAL_STATE as STATION_INITIAL_STATE } from './station.reducer';
import { INITIAL_STATE as SMS_VERIFICATION_INITIAL_STATE } from './sms-verification.reducer';
import { INITIAL_STATE as FLIGHT_INFO_INITIAL_STATE } from './flight-info.reducer';
import { INITIAL_STATE as PREVIOUS_ROUTE_INITIAL_STATE } from './previous-route.reducer';
import { INITIAL_STATE as HEADERS_INITIAL_STATE } from './headers.reducer';
import { INITIAL_STATE as APO_INITIAL_STATE } from './apo.reducer';
import { localStorageSync } from 'ngrx-store-localstorage';
import { AppActionTypes } from '@actions/app.action';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { DateTime } from 'luxon';

export interface CoreState {
  apo: ApoState;
  app: AppState;
  availability: AvailabilityState;
  bags: BagsState;
  booking: BookingState;
  cms: CmsState;
  culture: CultureState;
  extras: ExtrasState;
  flightInfo: FlightInfoState;
  headers: HeadersState;
  meals: MealsState;
  payment: PaymentState;
  previousRoute: PreviousRouteState;
  resource: ResourceState;
  router: RouterReducerState;
  seat: SeatState;
  smsVerification: SmsVerificationState;
  station: StationState;
  user: UserState;
}

export const reducers: ActionReducerMap<CoreState> = {
  app: appReducer,
  cms: cmsReducer,
  availability: availabilityReducer,
  culture: cultureReducer,
  booking: bookingReducer,
  user: userReducer,
  bags: bagsReducer,
  extras: extrasReducer,
  seat: seatReducer,
  meals: mealsReducer,
  resource: resourceReducer,
  router: routerReducer,
  payment: paymentReducer,
  smsVerification: smsVerificationReducer,
  station: stationReducer,
  flightInfo: flightInfoReducer,
  previousRoute: previousRouteReducer,
  headers: headersReducer,
  apo: apoReducer,
};

// NOTE: Remember to use keyof below, it prevents subtle bugs
// where data isn't persisted because the key isn't a valid
// property of the store.
export function localStorageSyncReducer(
  reducer: ActionReducer<CoreState>,
): ActionReducer<CoreState> {
  return localStorageSync({
    keys: [
      {
        app: [
          'pathSteps',
          // below has to be persisted as it will come back
          // as zero when the token is being refreshed, so we
          // have no way of knowing what the timeout originally
          // was on a page refresh otherwise.
          'tokenTimeoutInMinutes',
          'dismissedNotifications',
        ] as (keyof AppState)[],
      },
      {
        availability: {
          filter: [
              'searchInput',
              'searchAvailabilityInput',
              'selectedDates',
              'activeWeek',
              'displayView',
              'tripType',
              'selection',
              'availability',
              'lowFareAvailability',
              'sourceOrganization',
              'redirectId',
              'autoSelect',
              'isSearchByQuery'
            ] as (keyof AvailabilityState)[],
          reviver: availabilityReviver
        }
      },
      {
        booking: [
          'ssrAvailability',
          'booking',
          'originalBooking',
          'checkInPassengers',
          'travelersToChange',
          'dangerousGoodsViewed',
        ] as (keyof BookingState)[],
      },
      {
        payment: [
          'paymentStatusReference'
        ] as (keyof PaymentState)[]
      },
      'resource',
      {
        culture: [
          'geolocationCultureCode',
          'selectedCultureCode',
          'urlCultureCode',
          'selectedCurrencyCode',
        ] as (keyof CultureState)[],
      },
    ] as (keyof CoreState)[],
    rehydrate: true,
    restoreDates: false,
    storage: sessionStorage,

  })(reducer);
}

const availabilityReviver = (key: string, value: unknown) => {
  if (key === 'selectedDates' && Array.isArray(value)) {
    return value.map((dateString: string) => DateTime.fromISO(dateString));
  }
  return value;
};

export function resetStateReducer(
  reducer: ActionReducer<CoreState>,
): ActionReducer<CoreState> {
  return function (state: CoreState, action: Action): CoreState {
    if (action.type === AppActionTypes.resetApp.toString()) {
      const {
        resource,
        culture,
        router,
        availability: { searchInput, searchAvailabilityInput },
        cms,
      } = state;
      // leave all non-session-specific data in the store so we don't have to refetch it for no reason
      const newState = {
        ...INITIAL_STATE,
        ...{
          resource,
          culture,
          router,
          availability: {
            ...INITIAL_STATE.availability,
            searchInput,
            searchAvailabilityInput,
          },
          cms,
        },
      } as CoreState;
      return reducer(newState, action);
    }
    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer<CoreState>[] = [
  localStorageSyncReducer,
  resetStateReducer,
];

/* istanbul ignore next  */
if (environment.production) {
  // FUTURE: push meta-reducers for production
  metaReducers.push();
}

export const selectCoreFeatureState = createFeatureSelector<CoreState>('core');
export const selectAppFeatureState = createFeatureSelector<AppState>('app');
export const selectCmsFeatureState = createFeatureSelector<CmsState>('cms');
export const selectStationFeatureState =
  createFeatureSelector<StationState>('station');
export const selectSmsVerificationFeatureState =
  createFeatureSelector<SmsVerificationState>('smsVerification');
export const selectAvailabilityFeatureState =
  createFeatureSelector<AvailabilityState>('availability');
export const selectUserFeatureState = createFeatureSelector<UserState>('user');
export const selectCultureFeatureState =
  createFeatureSelector<CultureState>('culture');
export const selectBookingFeatureState =
  createFeatureSelector<BookingState>('booking');
export const selectBagsFeatureState = createFeatureSelector<BagsState>('bags');
export const selectExtrasFeatureState =
  createFeatureSelector<ExtrasState>('extras');
export const selectSeatFeatureState = createFeatureSelector<SeatState>('seat');
export const selectMealsFeatureState =
  createFeatureSelector<MealsState>('meals');
export const selectResourceFeatureState =
  createFeatureSelector<ResourceState>('resource');
export const selectPaymentFeatureState =
  createFeatureSelector<PaymentState>('payment');
export const selectFlightInfoFeatureState =
  createFeatureSelector<FlightInfoState>('flightInfo');
export const previousFeatureRouteState =
  createFeatureSelector<PreviousRouteState>('previousRoute');
export const selectHeadersFeatureState =
  createFeatureSelector<HeadersState>('headers');
export const selectApoState =
  createFeatureSelector<ApoState>('apo');
// export const selectUpgradeFlowState =
//   createFeatureSelector<UpgradeFlowState>('upgradeFlow');

export const INITIAL_STATE: CoreState = {
  app: APP_INITIAL_STATE,
  cms: CMS_INITIAL_STATE,
  availability: AVAILABILITY_INITIAL_STATE,
  culture: CULTURE_INITIAL_STATE,
  booking: BOOKING_INITIAL_STATE,
  user: USER_INITIAL_STATE,
  bags: BAGS_INITIAL_STATE,
  extras: EXTRAS_INITIAL_STATE,
  seat: SEAT_INITIAL_STATE,
  meals: MEALS_INITIAL_STATE,
  resource: RESOURCE_INITIAL_STATE,
  router: {} as RouterReducerState,
  payment: PAYMENT_INITIAL_STATE,
  smsVerification: SMS_VERIFICATION_INITIAL_STATE,
  station: STATION_INITIAL_STATE,
  flightInfo: FLIGHT_INFO_INITIAL_STATE,
  previousRoute: PREVIOUS_ROUTE_INITIAL_STATE,
  headers: HEADERS_INITIAL_STATE,
  apo: APO_INITIAL_STATE
};

export const INITIAL_TEST_STATE: CoreState = {
  ...INITIAL_STATE,
  culture: {
    ...INITIAL_STATE.culture,
  },
  resource: {
    ...INITIAL_STATE.resource,
    phoneCodes: [
      {
        countryCode: 'US',
        name: 'US',
        value: '1',
      },
    ],
  },
};
